/**
 * Firestore Helper Functions for Dual-Write Migration
 *
 * These functions write to both old (flat) and new (hierarchical) structures
 * during the migration period. Once migration is complete, we'll remove the
 * dual-write code and only use the new structure.
 *
 * Usage:
 *   import { dualWriteJob, dualReadJobs } from '@/lib/firestore-helpers';
 *
 *   // Create a job (writes to both structures)
 *   await dualWriteJob(contractorId, jobId, jobData);
 *
 *   // Read jobs (reads from old structure for now)
 *   const jobs = await dualReadJobs(contractorId);
 */

import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  QueryConstraint,
  DocumentData,
  CollectionReference,
  DocumentReference,
  Timestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from './firebase';

// Initialize Firebase Functions
const functions = getFunctions();

// Cloud function to send document notification emails
const sendDocumentEmailFn = httpsCallable(functions, 'sendDocumentEmail');

// ============================================
// CONFIGURATION
// ============================================

// Toggle between old and new structure for reads
// Set to 'new' once migration is complete
export const READ_MODE: 'old' | 'new' = 'old';

// Enable/disable dual writes
// Set to false once migration is complete
export const ENABLE_DUAL_WRITE = true;

// ============================================
// HELPER TYPES
// ============================================

interface DualWriteOptions {
  skipOldWrite?: boolean;  // Skip writing to old structure (for new features)
  skipNewWrite?: boolean;  // Skip writing to new structure (for rollback)
}

// ============================================
// JOBS COLLECTION
// ============================================

/**
 * Create or update a job in both old and new structures
 */
export async function dualWriteJob(
  contractorId: string,
  jobId: string,
  jobData: DocumentData,
  options: DualWriteOptions = {}
) {
  const dataWithTimestamp = {
    ...jobData,
    updatedAt: Timestamp.now(),
  };

  // Write to new structure
  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/jobs/${jobId}`);
    await setDoc(newRef, dataWithTimestamp, { merge: true });
  }

  // Write to old structure (include contractorId)
  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `jobs/${jobId}`);
    await setDoc(oldRef, {
      ...dataWithTimestamp,
      contractorId,  // Include contractorId in old structure
    }, { merge: true });
  }
}

/**
 * Soft delete a job (marks as deleted but preserves data for training/analytics)
 * Sets deletedAt timestamp instead of actually deleting
 */
export async function dualSoftDeleteJob(
  contractorId: string,
  jobId: string,
  deletedBy: string,
  options: DualWriteOptions = {}
) {
  const softDeleteData = {
    deletedAt: Timestamp.now(),
    deletedBy,
    isDeleted: true,
  };

  const errors: Error[] = [];

  // Soft delete in new structure
  if (!options.skipNewWrite) {
    try {
      const newRef = doc(db, `contractors/${contractorId}/jobs/${jobId}`);
      await setDoc(newRef, softDeleteData, { merge: true });
    } catch (err) {
      console.error('Failed to soft delete in new structure:', err);
      errors.push(err as Error);
    }
  }

  // Soft delete in old structure
  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    try {
      const oldRef = doc(db, `jobs/${jobId}`);
      await setDoc(oldRef, softDeleteData, { merge: true });
    } catch (err) {
      console.error('Failed to soft delete in old structure:', err);
      errors.push(err as Error);
    }
  }

  // If both writes failed, throw the first error
  if (errors.length === 2) {
    throw errors[0];
  }
  // If at least one succeeded, consider it a success (for migration compatibility)
}

/**
 * Hard delete a job from both structures (use sparingly - prefer soft delete)
 * @deprecated Use dualSoftDeleteJob instead to preserve data for training/analytics
 */
export async function dualDeleteJob(
  contractorId: string,
  jobId: string,
  options: DualWriteOptions = {}
) {
  // Delete from new structure
  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/jobs/${jobId}`);
    await deleteDoc(newRef);
  }

  // Delete from old structure
  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `jobs/${jobId}`);
    await deleteDoc(oldRef);
  }
}

/**
 * Read jobs (respects READ_MODE configuration)
 */
export async function dualReadJobs(
  contractorId: string,
  constraints: QueryConstraint[] = []
) {
  if (READ_MODE === 'new') {
    // Read from new structure
    const q = query(
      collection(db, `contractors/${contractorId}/jobs`),
      ...constraints
    );
    return getDocs(q);
  } else {
    // Read from old structure
    const q = query(
      collection(db, 'jobs'),
      where('contractorId', '==', contractorId),
      ...constraints
    );
    return getDocs(q);
  }
}

/**
 * Read a single job
 */
export async function dualReadJob(contractorId: string, jobId: string) {
  if (READ_MODE === 'new') {
    const ref = doc(db, `contractors/${contractorId}/jobs/${jobId}`);
    return await getDoc(ref);
  } else {
    const ref = doc(db, `jobs/${jobId}`);
    return await getDoc(ref);
  }
}

// ============================================
// ESTIMATES COLLECTION
// ============================================

export async function dualWriteEstimate(
  contractorId: string,
  estimateId: string,
  estimateData: DocumentData,
  options: DualWriteOptions = {}
) {
  const dataWithTimestamp = {
    ...estimateData,
    updatedAt: Timestamp.now(),
  };

  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/estimates/${estimateId}`);
    await setDoc(newRef, dataWithTimestamp, { merge: true });
  }

  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `estimates/${estimateId}`);
    await setDoc(oldRef, {
      ...dataWithTimestamp,
      contractorId,
    }, { merge: true });
  }
}

export async function dualDeleteEstimate(
  contractorId: string,
  estimateId: string,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/estimates/${estimateId}`);
    await deleteDoc(newRef);
  }

  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `estimates/${estimateId}`);
    await deleteDoc(oldRef);
  }
}

export async function dualReadEstimates(
  contractorId: string,
  constraints: QueryConstraint[] = []
) {
  if (READ_MODE === 'new') {
    const q = query(
      collection(db, `contractors/${contractorId}/estimates`),
      ...constraints
    );
    return await getDocs(q);
  } else {
    const q = query(
      collection(db, 'estimates'),
      where('contractorId', '==', contractorId),
      ...constraints
    );
    return await getDocs(q);
  }
}

export async function dualReadEstimate(contractorId: string, estimateId: string) {
  if (READ_MODE === 'new') {
    const ref = doc(db, `contractors/${contractorId}/estimates/${estimateId}`);
    return await getDoc(ref);
  } else {
    const ref = doc(db, `estimates/${estimateId}`);
    return await getDoc(ref);
  }
}

// ============================================
// ESTIMATE ITEMS SUBCOLLECTION
// ============================================

export async function dualWriteEstimateItem(
  contractorId: string,
  estimateId: string,
  itemId: string,
  itemData: DocumentData,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/estimates/${estimateId}/items/${itemId}`);
    await setDoc(newRef, itemData, { merge: true });
  }

  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `estimate_items/${itemId}`);
    await setDoc(oldRef, {
      ...itemData,
      estimateId,
      contractorId,
    }, { merge: true });
  }
}

export async function dualDeleteEstimateItem(
  contractorId: string,
  estimateId: string,
  itemId: string,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/estimates/${estimateId}/items/${itemId}`);
    await deleteDoc(newRef);
  }

  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `estimate_items/${itemId}`);
    await deleteDoc(oldRef);
  }
}

export async function dualReadEstimateItems(
  contractorId: string,
  estimateId: string
) {
  if (READ_MODE === 'new') {
    const q = query(collection(db, `contractors/${contractorId}/estimates/${estimateId}/items`));
    return await getDocs(q);
  } else {
    const q = query(
      collection(db, 'estimate_items'),
      where('estimateId', '==', estimateId)
    );
    return await getDocs(q);
  }
}

// ============================================
// ESTIMATE PHOTOS SUBCOLLECTION
// ============================================

export async function dualWriteEstimatePhoto(
  contractorId: string,
  estimateId: string,
  photoId: string,
  photoData: DocumentData,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/estimates/${estimateId}/photos/${photoId}`);
    await setDoc(newRef, photoData, { merge: true });
  }

  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `estimate_photos/${photoId}`);
    await setDoc(oldRef, {
      ...photoData,
      estimateId,
      contractorId,
    }, { merge: true });
  }
}

export async function dualDeleteEstimatePhoto(
  contractorId: string,
  estimateId: string,
  photoId: string,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/estimates/${estimateId}/photos/${photoId}`);
    await deleteDoc(newRef);
  }

  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `estimate_photos/${photoId}`);
    await deleteDoc(oldRef);
  }
}

export async function dualReadEstimatePhotos(
  contractorId: string,
  estimateId: string
) {
  if (READ_MODE === 'new') {
    const q = query(collection(db, `contractors/${contractorId}/estimates/${estimateId}/photos`));
    return await getDocs(q);
  } else {
    const q = query(
      collection(db, 'estimate_photos'),
      where('estimateId', '==', estimateId)
    );
    return await getDocs(q);
  }
}

// ============================================
// EXPENSES COLLECTION
// ============================================

export async function dualWriteExpense(
  contractorId: string,
  expenseId: string,
  expenseData: DocumentData,
  options: DualWriteOptions = {}
) {
  const dataWithTimestamp = {
    ...expenseData,
    updatedAt: Timestamp.now(),
  };

  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/expenses/${expenseId}`);
    await setDoc(newRef, dataWithTimestamp, { merge: true });
  }

  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `expenses/${expenseId}`);
    await setDoc(oldRef, {
      ...dataWithTimestamp,
      contractorId,
    }, { merge: true });
  }
}

export async function dualDeleteExpense(
  contractorId: string,
  expenseId: string,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/expenses/${expenseId}`);
    await deleteDoc(newRef);
  }

  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `expenses/${expenseId}`);
    await deleteDoc(oldRef);
  }
}

export async function dualReadExpenses(
  contractorId: string,
  constraints: QueryConstraint[] = []
) {
  if (READ_MODE === 'new') {
    const q = query(
      collection(db, `contractors/${contractorId}/expenses`),
      ...constraints
    );
    return await getDocs(q);
  } else {
    const q = query(
      collection(db, 'expenses'),
      where('contractorId', '==', contractorId),
      ...constraints
    );
    return await getDocs(q);
  }
}

export async function dualReadExpense(contractorId: string, expenseId: string) {
  if (READ_MODE === 'new') {
    const ref = doc(db, `contractors/${contractorId}/expenses/${expenseId}`);
    return await getDoc(ref);
  } else {
    const ref = doc(db, `expenses/${expenseId}`);
    return await getDoc(ref);
  }
}

// ============================================
// RECURRING EXPENSES COLLECTION
// ============================================

export async function dualWriteRecurringExpense(
  contractorId: string,
  recurringExpenseId: string,
  expenseData: DocumentData,
  options: DualWriteOptions = {}
) {
  const dataWithTimestamp = {
    ...expenseData,
    updatedAt: Timestamp.now(),
  };

  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/recurring_expenses/${recurringExpenseId}`);
    await setDoc(newRef, dataWithTimestamp, { merge: true });
  }

  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `recurring_expenses/${recurringExpenseId}`);
    await setDoc(oldRef, {
      ...dataWithTimestamp,
      userId: contractorId,
    }, { merge: true });
  }
}

export async function dualReadRecurringExpenses(
  contractorId: string,
  constraints: QueryConstraint[] = []
) {
  if (READ_MODE === 'new') {
    const q = query(
      collection(db, `contractors/${contractorId}/recurring_expenses`),
      ...constraints
    );
    return await getDocs(q);
  } else {
    const q = query(
      collection(db, 'recurring_expenses'),
      where('userId', '==', contractorId),
      ...constraints
    );
    return await getDocs(q);
  }
}

export async function dualDeleteRecurringExpense(contractorId: string, recurringExpenseId: string) {
  const newRef = doc(db, `contractors/${contractorId}/recurring_expenses/${recurringExpenseId}`);
  const oldRef = doc(db, `recurring_expenses/${recurringExpenseId}`);

  if (ENABLE_DUAL_WRITE) {
    await Promise.all([
      deleteDoc(newRef),
      deleteDoc(oldRef),
    ]);
  } else if (READ_MODE === 'new') {
    await deleteDoc(newRef);
  } else {
    await deleteDoc(oldRef);
  }
}

// ============================================
// APPOINTMENTS COLLECTION
// ============================================

export async function dualWriteAppointment(
  contractorId: string,
  appointmentId: string,
  appointmentData: DocumentData,
  options: DualWriteOptions = {}
) {
  const dataWithTimestamp = {
    ...appointmentData,
    updatedAt: Timestamp.now(),
  };

  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/appointments/${appointmentId}`);
    await setDoc(newRef, dataWithTimestamp, { merge: true });
  }

  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `appointments/${appointmentId}`);
    await setDoc(oldRef, {
      ...dataWithTimestamp,
      contractorId,
    }, { merge: true });
  }
}

export async function dualReadAppointments(
  contractorId: string,
  constraints: QueryConstraint[] = []
) {
  if (READ_MODE === 'new') {
    const q = query(
      collection(db, `contractors/${contractorId}/appointments`),
      ...constraints
    );
    return await getDocs(q);
  } else {
    const q = query(
      collection(db, 'appointments'),
      where('contractorId', '==', contractorId),
      ...constraints
    );
    return await getDocs(q);
  }
}

export async function dualReadAppointment(contractorId: string, appointmentId: string) {
  if (READ_MODE === 'new') {
    const ref = doc(db, `contractors/${contractorId}/appointments/${appointmentId}`);
    return await getDoc(ref);
  } else {
    const ref = doc(db, `appointments/${appointmentId}`);
    return await getDoc(ref);
  }
}

export async function dualDeleteAppointment(contractorId: string, appointmentId: string) {
  const newRef = doc(db, `contractors/${contractorId}/appointments/${appointmentId}`);
  const oldRef = doc(db, `appointments/${appointmentId}`);

  if (ENABLE_DUAL_WRITE) {
    await Promise.all([
      deleteDoc(newRef),
      deleteDoc(oldRef),
    ]);
  } else if (READ_MODE === 'new') {
    await deleteDoc(newRef);
  } else {
    await deleteDoc(oldRef);
  }
}

// ============================================
// AVAILABILITY SLOTS COLLECTION
// ============================================

export async function dualWriteAvailabilitySlot(
  contractorId: string,
  slotId: string,
  slotData: DocumentData,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/availabilitySlots/${slotId}`);
    await setDoc(newRef, slotData, { merge: true });
  }

  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `availabilitySlots/${slotId}`);
    await setDoc(oldRef, {
      ...slotData,
      contractorId,
    }, { merge: true });
  }
}

export async function dualReadAvailabilitySlots(
  contractorId: string,
  constraints: QueryConstraint[] = []
) {
  if (READ_MODE === 'new') {
    const q = query(
      collection(db, `contractors/${contractorId}/availabilitySlots`),
      ...constraints
    );
    return await getDocs(q);
  } else {
    const q = query(
      collection(db, 'availabilitySlots'),
      where('contractorId', '==', contractorId),
      ...constraints
    );
    return await getDocs(q);
  }
}

export async function dualDeleteAvailabilitySlot(contractorId: string, slotId: string) {
  const newRef = doc(db, `contractors/${contractorId}/availabilitySlots/${slotId}`);
  const oldRef = doc(db, `availabilitySlots/${slotId}`);

  if (ENABLE_DUAL_WRITE) {
    await Promise.all([
      deleteDoc(newRef),
      deleteDoc(oldRef),
    ]);
  } else if (READ_MODE === 'new') {
    await deleteDoc(newRef);
  } else {
    await deleteDoc(oldRef);
  }
}

// ============================================
// TIME ENTRIES COLLECTION
// ============================================

export async function dualWriteTimeEntry(
  contractorId: string,
  entryId: string,
  entryData: DocumentData,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/timeEntries/${entryId}`);
    await setDoc(newRef, entryData, { merge: true });
  }

  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `timeEntries/${entryId}`);
    await setDoc(oldRef, {
      ...entryData,
      contractorId,
    }, { merge: true });
  }
}

export async function dualReadTimeEntries(
  contractorId: string,
  constraints: QueryConstraint[] = []
) {
  if (READ_MODE === 'new') {
    const q = query(
      collection(db, `contractors/${contractorId}/timeEntries`),
      ...constraints
    );
    return await getDocs(q);
  } else {
    const q = query(
      collection(db, 'timeEntries'),
      where('contractorId', '==', contractorId),
      ...constraints
    );
    return await getDocs(q);
  }
}

// ============================================
// MATERIAL ORDERS COLLECTION
// ============================================

export async function dualWriteMaterialOrder(
  contractorId: string,
  orderId: string,
  orderData: DocumentData,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/material_orders/${orderId}`);
    await setDoc(newRef, orderData, { merge: true });
  }

  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `material_orders/${orderId}`);
    await setDoc(oldRef, {
      ...orderData,
      contractorId,
    }, { merge: true });
  }
}

export async function dualReadMaterialOrders(
  contractorId: string,
  constraints: QueryConstraint[] = []
) {
  if (READ_MODE === 'new') {
    const q = query(
      collection(db, `contractors/${contractorId}/material_orders`),
      ...constraints
    );
    return await getDocs(q);
  } else {
    const q = query(
      collection(db, 'material_orders'),
      where('contractorId', '==', contractorId),
      ...constraints
    );
    return await getDocs(q);
  }
}

// ============================================
// AI CONVERSATIONS COLLECTION
// ============================================

export async function dualWriteAIConversation(
  contractorId: string,
  conversationId: string,
  conversationData: DocumentData,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/ai_conversations/${conversationId}`);
    await setDoc(newRef, conversationData, { merge: true });
  }

  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `ai_conversations/${conversationId}`);
    await setDoc(oldRef, {
      ...conversationData,
      contractorId,
    }, { merge: true });
  }
}

export async function dualReadAIConversations(
  contractorId: string,
  constraints: QueryConstraint[] = []
) {
  if (READ_MODE === 'new') {
    const q = query(
      collection(db, `contractors/${contractorId}/ai_conversations`),
      ...constraints
    );
    return await getDocs(q);
  } else {
    const q = query(
      collection(db, 'ai_conversations'),
      where('contractorId', '==', contractorId),
      ...constraints
    );
    return await getDocs(q);
  }
}

export async function dualDeleteAIConversation(
  contractorId: string,
  conversationId: string,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    await deleteDoc(doc(db, `contractors/${contractorId}/ai_conversations/${conversationId}`));
  }
  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    await deleteDoc(doc(db, `ai_conversations/${conversationId}`));
  }
}

// ============================================
// QUOTES COLLECTION
// ============================================

export async function dualWriteQuote(
  contractorId: string,
  quoteId: string,
  quoteData: DocumentData,
  options: DualWriteOptions = {}
) {
  const dataWithTimestamp = {
    ...quoteData,
    updatedAt: Timestamp.now(),
  };

  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/quotes/${quoteId}`);
    await setDoc(newRef, dataWithTimestamp, { merge: true });
  }

  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `quotes/${quoteId}`);
    await setDoc(oldRef, {
      ...dataWithTimestamp,
      created_by: contractorId,  // Use created_by field for contractor ID in quotes
    }, { merge: true });
  }
}

export async function dualDeleteQuote(
  contractorId: string,
  quoteId: string,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/quotes/${quoteId}`);
    await deleteDoc(newRef);
  }

  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `quotes/${quoteId}`);
    await deleteDoc(oldRef);
  }
}

export async function dualReadQuotes(
  contractorId: string,
  constraints: QueryConstraint[] = []
) {
  if (READ_MODE === 'new') {
    const q = query(
      collection(db, `contractors/${contractorId}/quotes`),
      ...constraints
    );
    return await getDocs(q);
  } else {
    const q = query(
      collection(db, 'quotes'),
      where('created_by', '==', contractorId),
      ...constraints
    );
    return await getDocs(q);
  }
}

export async function dualReadQuote(contractorId: string, quoteId: string) {
  if (READ_MODE === 'new') {
    const ref = doc(db, `contractors/${contractorId}/quotes/${quoteId}`);
    return await getDoc(ref);
  } else {
    const ref = doc(db, `quotes/${quoteId}`);
    return await getDoc(ref);
  }
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get collection reference based on READ_MODE
 */
export function getCollectionRef(
  contractorId: string,
  collectionName: string
): CollectionReference {
  if (READ_MODE === 'new') {
    return collection(db, `contractors/${contractorId}/${collectionName}`);
  } else {
    return collection(db, collectionName);
  }
}

/**
 * Get document reference based on READ_MODE
 */
export function getDocRef(
  contractorId: string,
  collectionName: string,
  docId: string
): DocumentReference {
  if (READ_MODE === 'new') {
    return doc(db, `contractors/${contractorId}/${collectionName}/${docId}`);
  } else {
    return doc(db, collectionName, docId);
  }
}

// ============================================
// JOB SUBCOLLECTIONS (photos, documents, messages, etc.)
// ============================================

/**
 * Write to job photos subcollection
 * Old: job_photos collection with job_id field
 * New: contractors/{contractorId}/jobs/{jobId}/photos/{photoId}
 */
export async function dualWriteJobPhoto(
  contractorId: string,
  jobId: string,
  photoId: string,
  photoData: DocumentData,
  options: DualWriteOptions = {}
) {
  const dataWithTimestamp = {
    ...photoData,
    updatedAt: Timestamp.now(),
  };

  // Write to new structure (subcollection)
  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/jobs/${jobId}/photos/${photoId}`);
    await setDoc(newRef, dataWithTimestamp, { merge: true });
  }

  // Write to old structure (separate collection with job_id)
  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `job_photos/${photoId}`);
    await setDoc(oldRef, {
      ...dataWithTimestamp,
      job_id: jobId,
      contractorId,
    }, { merge: true });
  }
}

export async function dualReadJobPhotos(
  contractorId: string,
  jobId: string,
  constraints: QueryConstraint[] = []
) {
  if (READ_MODE === 'new') {
    const q = query(
      collection(db, `contractors/${contractorId}/jobs/${jobId}/photos`),
      ...constraints
    );
    return getDocs(q);
  } else {
    const q = query(
      collection(db, 'job_photos'),
      where('job_id', '==', jobId),
      ...constraints
    );
    return getDocs(q);
  }
}

export async function dualDeleteJobPhoto(
  contractorId: string,
  jobId: string,
  photoId: string,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    await deleteDoc(doc(db, `contractors/${contractorId}/jobs/${jobId}/photos/${photoId}`));
  }
  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    await deleteDoc(doc(db, `job_photos/${photoId}`));
  }
}

/**
 * Write to job documents subcollection
 */
export async function dualWriteJobDocument(
  contractorId: string,
  jobId: string,
  documentId: string,
  documentData: DocumentData,
  options: DualWriteOptions = {}
) {
  const dataWithTimestamp = {
    ...documentData,
    updatedAt: Timestamp.now(),
  };

  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/jobs/${jobId}/documents/${documentId}`);
    await setDoc(newRef, dataWithTimestamp, { merge: true });
  }

  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `job_documents/${documentId}`);
    await setDoc(oldRef, {
      ...dataWithTimestamp,
      job_id: jobId,
      contractorId,
    }, { merge: true });
  }
}

export async function dualReadJobDocuments(
  contractorId: string,
  jobId: string,
  constraints: QueryConstraint[] = []
) {
  if (READ_MODE === 'new') {
    const q = query(
      collection(db, `contractors/${contractorId}/jobs/${jobId}/documents`),
      ...constraints
    );
    return getDocs(q);
  } else {
    const q = query(
      collection(db, 'job_documents'),
      where('job_id', '==', jobId),
      ...constraints
    );
    return getDocs(q);
  }
}

export async function dualDeleteJobDocument(
  contractorId: string,
  jobId: string,
  documentId: string,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    await deleteDoc(doc(db, `contractors/${contractorId}/jobs/${jobId}/documents/${documentId}`));
  }
  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    await deleteDoc(doc(db, `job_documents/${documentId}`));
  }
}

/**
 * Write to job messages subcollection
 */
export async function dualWriteJobMessage(
  contractorId: string,
  jobId: string,
  messageId: string,
  messageData: DocumentData,
  options: DualWriteOptions = {}
) {
  const dataWithTimestamp = {
    ...messageData,
    updatedAt: Timestamp.now(),
  };

  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/jobs/${jobId}/messages/${messageId}`);
    await setDoc(newRef, dataWithTimestamp, { merge: true });
  }

  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `job_messages/${messageId}`);
    await setDoc(oldRef, {
      ...dataWithTimestamp,
      job_id: jobId,
      contractorId,
    }, { merge: true });
  }
}

export async function dualReadJobMessages(
  contractorId: string,
  jobId: string,
  constraints: QueryConstraint[] = []
) {
  if (READ_MODE === 'new') {
    const q = query(
      collection(db, `contractors/${contractorId}/jobs/${jobId}/messages`),
      ...constraints
    );
    return getDocs(q);
  } else {
    const q = query(
      collection(db, 'job_messages'),
      where('job_id', '==', jobId),
      ...constraints
    );
    return getDocs(q);
  }
}

export async function dualDeleteJobMessage(
  contractorId: string,
  jobId: string,
  messageId: string,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    await deleteDoc(doc(db, `contractors/${contractorId}/jobs/${jobId}/messages/${messageId}`));
  }
  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    await deleteDoc(doc(db, `job_messages/${messageId}`));
  }
}

/**
 * Write to daily reports subcollection
 */
export async function dualWriteDailyReport(
  contractorId: string,
  jobId: string,
  reportId: string,
  reportData: DocumentData,
  options: DualWriteOptions = {}
) {
  const dataWithTimestamp = {
    ...reportData,
    updatedAt: Timestamp.now(),
  };

  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/jobs/${jobId}/daily_reports/${reportId}`);
    await setDoc(newRef, dataWithTimestamp, { merge: true });
  }

  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `daily_reports/${reportId}`);
    await setDoc(oldRef, {
      ...dataWithTimestamp,
      job_id: jobId,
      contractorId,
    }, { merge: true });
  }
}

export async function dualReadDailyReports(
  contractorId: string,
  jobId: string,
  constraints: QueryConstraint[] = []
) {
  if (READ_MODE === 'new') {
    const q = query(
      collection(db, `contractors/${contractorId}/jobs/${jobId}/daily_reports`),
      ...constraints
    );
    return getDocs(q);
  } else {
    const q = query(
      collection(db, 'daily_reports'),
      where('job_id', '==', jobId),
      ...constraints
    );
    return getDocs(q);
  }
}

export async function dualDeleteDailyReport(
  contractorId: string,
  jobId: string,
  reportId: string,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    await deleteDoc(doc(db, `contractors/${contractorId}/jobs/${jobId}/daily_reports/${reportId}`));
  }
  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    await deleteDoc(doc(db, `daily_reports/${reportId}`));
  }
}

/**
 * Write to extra works subcollection
 */
export async function dualWriteExtraWork(
  contractorId: string,
  jobId: string,
  workId: string,
  workData: DocumentData,
  options: DualWriteOptions = {}
) {
  const dataWithTimestamp = {
    ...workData,
    updatedAt: Timestamp.now(),
  };

  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/jobs/${jobId}/extra_works/${workId}`);
    await setDoc(newRef, dataWithTimestamp, { merge: true });
  }

  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `extra_works/${workId}`);
    await setDoc(oldRef, {
      ...dataWithTimestamp,
      job_id: jobId,
      contractorId,
    }, { merge: true });
  }
}

export async function dualReadExtraWorks(
  contractorId: string,
  jobId: string,
  constraints: QueryConstraint[] = []
) {
  if (READ_MODE === 'new') {
    const q = query(
      collection(db, `contractors/${contractorId}/jobs/${jobId}/extra_works`),
      ...constraints
    );
    return getDocs(q);
  } else {
    const q = query(
      collection(db, 'extra_works'),
      where('job_id', '==', jobId),
      ...constraints
    );
    return getDocs(q);
  }
}

export async function dualDeleteExtraWork(
  contractorId: string,
  jobId: string,
  workId: string,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    await deleteDoc(doc(db, `contractors/${contractorId}/jobs/${jobId}/extra_works/${workId}`));
  }
  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    await deleteDoc(doc(db, `extra_works/${workId}`));
  }
}

// ============================================
// JUNCTION TABLES
// ============================================

/**
 * Job Assignments (technicians assigned to jobs)
 * Old: job_assignments collection
 * New: Could be subcollection OR separate collection - keeping as separate for now
 */
export async function dualWriteJobAssignment(
  contractorId: string,
  assignmentId: string,
  assignmentData: DocumentData,
  options: DualWriteOptions = {}
) {
  const dataWithTimestamp = {
    ...assignmentData,
    updatedAt: Timestamp.now(),
  };

  // Write to new structure
  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/job_assignments/${assignmentId}`);
    await setDoc(newRef, dataWithTimestamp, { merge: true });
  }

  // Write to old structure
  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `job_assignments/${assignmentId}`);
    await setDoc(oldRef, {
      ...dataWithTimestamp,
      contractorId,
    }, { merge: true });
  }
}

export async function dualReadJobAssignments(
  contractorId: string,
  jobId: string,
  constraints: QueryConstraint[] = []
) {
  if (READ_MODE === 'new') {
    const q = query(
      collection(db, `contractors/${contractorId}/job_assignments`),
      where('job_id', '==', jobId),
      ...constraints
    );
    return getDocs(q);
  } else {
    const q = query(
      collection(db, 'job_assignments'),
      where('job_id', '==', jobId),
      ...constraints
    );
    return getDocs(q);
  }
}

export async function dualDeleteJobAssignment(
  contractorId: string,
  assignmentId: string,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    await deleteDoc(doc(db, `contractors/${contractorId}/job_assignments/${assignmentId}`));
  }
  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    await deleteDoc(doc(db, `job_assignments/${assignmentId}`));
  }
}

/**
 * Payment Milestones
 */
export async function dualWritePaymentMilestone(
  contractorId: string,
  milestoneId: string,
  milestoneData: DocumentData,
  options: DualWriteOptions = {}
) {
  const dataWithTimestamp = {
    ...milestoneData,
    updatedAt: Timestamp.now(),
  };

  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/paymentMilestones/${milestoneId}`);
    await setDoc(newRef, dataWithTimestamp, { merge: true });
  }

  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `paymentMilestones/${milestoneId}`);
    await setDoc(oldRef, {
      ...dataWithTimestamp,
      contractorId,
    }, { merge: true });
  }
}

export async function dualReadPaymentMilestones(
  contractorId: string,
  constraints: QueryConstraint[] = []
) {
  if (READ_MODE === 'new') {
    const q = query(
      collection(db, `contractors/${contractorId}/paymentMilestones`),
      ...constraints
    );
    return getDocs(q);
  } else {
    const q = query(
      collection(db, 'paymentMilestones'),
      where('contractorId', '==', contractorId),
      ...constraints
    );
    return getDocs(q);
  }
}

export async function dualDeletePaymentMilestone(
  contractorId: string,
  milestoneId: string,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    await deleteDoc(doc(db, `contractors/${contractorId}/paymentMilestones/${milestoneId}`));
  }
  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    await deleteDoc(doc(db, `paymentMilestones/${milestoneId}`));
  }
}

// ============================================
// PROPERTIES COLLECTION
// ============================================

/**
 * Create or update a property in both old and new structures
 */
export async function dualWriteProperty(
  contractorId: string,
  propertyId: string,
  propertyData: DocumentData,
  options: DualWriteOptions = {}
) {
  const dataWithTimestamp = {
    ...propertyData,
    updatedAt: Timestamp.now(),
  };

  // Write to new structure
  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/properties/${propertyId}`);
    await setDoc(newRef, dataWithTimestamp, { merge: true });
  }

  // Write to old structure (include contractorId)
  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `properties/${propertyId}`);
    await setDoc(oldRef, {
      ...dataWithTimestamp,
      contractorId,
    }, { merge: true });
  }
}

/**
 * Read properties from old or new structure based on READ_MODE
 */
export async function dualReadProperties(
  contractorId: string,
  constraints: QueryConstraint[] = []
) {
  if (READ_MODE === 'new') {
    const q = query(
      collection(db, `contractors/${contractorId}/properties`),
      ...constraints
    );
    return getDocs(q);
  } else {
    const q = query(
      collection(db, 'properties'),
      where('contractorId', '==', contractorId),
      ...constraints
    );
    return getDocs(q);
  }
}

/**
 * Read a single property from old or new structure
 */
export async function dualReadProperty(
  contractorId: string,
  propertyId: string
) {
  if (READ_MODE === 'new') {
    return getDoc(doc(db, `contractors/${contractorId}/properties/${propertyId}`));
  } else {
    return getDoc(doc(db, `properties/${propertyId}`));
  }
}

/**
 * Delete a property from both structures
 */
export async function dualDeleteProperty(
  contractorId: string,
  propertyId: string,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    await deleteDoc(doc(db, `contractors/${contractorId}/properties/${propertyId}`));
  }
  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    await deleteDoc(doc(db, `properties/${propertyId}`));
  }
}

// ============================================
// CLIENTS COLLECTION
// ============================================

/**
 * Create or update a client in both old and new structures
 */
export async function dualWriteClient(
  contractorId: string,
  clientId: string,
  clientData: DocumentData,
  options: DualWriteOptions = {}
) {
  const dataWithTimestamp = {
    ...clientData,
    updatedAt: Timestamp.now(),
  };

  // Write to new structure
  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/clients/${clientId}`);
    await setDoc(newRef, dataWithTimestamp, { merge: true });
  }

  // Write to old structure (include contractorId)
  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `clients/${clientId}`);
    await setDoc(oldRef, {
      ...dataWithTimestamp,
      contractorId,
    }, { merge: true });
  }
}

/**
 * Read clients from old or new structure based on READ_MODE
 */
export async function dualReadClients(
  contractorId: string,
  constraints: QueryConstraint[] = []
) {
  if (READ_MODE === 'new') {
    const q = query(
      collection(db, `contractors/${contractorId}/clients`),
      ...constraints
    );
    return getDocs(q);
  } else {
    const q = query(
      collection(db, 'clients'),
      where('contractorId', '==', contractorId),
      ...constraints
    );
    return getDocs(q);
  }
}

/**
 * Read a single client from old or new structure
 */
export async function dualReadClient(
  contractorId: string,
  clientId: string
) {
  if (READ_MODE === 'new') {
    return getDoc(doc(db, `contractors/${contractorId}/clients/${clientId}`));
  } else {
    return getDoc(doc(db, `clients/${clientId}`));
  }
}

/**
 * Delete a client from both structures
 */
export async function dualDeleteClient(
  contractorId: string,
  clientId: string,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    await deleteDoc(doc(db, `contractors/${contractorId}/clients/${clientId}`));
  }
  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    await deleteDoc(doc(db, `clients/${clientId}`));
  }
}

// ============================================================================
// NOTIFICATIONS
// ============================================================================

/**
 * Delete a notification from both structures
 */
export async function dualDeleteNotification(
  contractorId: string,
  notificationId: string,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    await deleteDoc(doc(db, `contractors/${contractorId}/notifications/${notificationId}`));
  }
  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    await deleteDoc(doc(db, `notifications/${notificationId}`));
  }
}

// ============================================================================
// DOCUMENTS
// ============================================================================

/**
 * Read documents from appropriate structure
 */
export async function dualReadDocuments(
  contractorId: string,
  constraints: QueryConstraint[] = []
) {
  const collectionPath = READ_MODE === 'new'
    ? `contractors/${contractorId}/documents`
    : 'documents';

  const q = query(collection(db, collectionPath), ...constraints);
  return await getDocs(q);
}

/**
 * Write a document to both structures
 */
export async function dualWriteDocument(
  contractorId: string,
  documentId: string,
  data: any,
  options: DualWriteOptions = {}
) {
  const timestamp = Timestamp.now();
  const documentData = {
    ...data,
    updatedAt: timestamp,
    ...(!data.createdAt && { createdAt: timestamp }),
  };

  if (!options.skipNewWrite) {
    await setDoc(
      doc(db, `contractors/${contractorId}/documents/${documentId}`),
      documentData,
      { merge: true }
    );
  }

  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    await setDoc(
      doc(db, `documents/${documentId}`),
      { ...documentData, contractorId },
      { merge: true }
    );
  }
}

/**
 * Delete a document from both structures
 */
export async function dualDeleteDocument(
  contractorId: string,
  documentId: string,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    await deleteDoc(doc(db, `contractors/${contractorId}/documents/${documentId}`));
  }
  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    await deleteDoc(doc(db, `documents/${documentId}`));
  }
}

// ============================================
// INVOICES COLLECTION
// ============================================

export async function dualWriteInvoice(
  contractorId: string,
  invoiceId: string,
  invoiceData: DocumentData,
  options: DualWriteOptions = {}
) {
  const dataWithTimestamp = {
    ...invoiceData,
    updatedAt: Timestamp.now(),
  };

  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/invoices/${invoiceId}`);
    await setDoc(newRef, dataWithTimestamp, { merge: true });
  }

  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `invoices/${invoiceId}`);
    await setDoc(oldRef, {
      ...dataWithTimestamp,
      created_by: contractorId,
    }, { merge: true });
  }
}

export async function dualReadInvoices(
  contractorId: string,
  constraints: QueryConstraint[] = []
) {
  if (READ_MODE === 'new') {
    const q = query(
      collection(db, `contractors/${contractorId}/invoices`),
      ...constraints
    );
    return await getDocs(q);
  } else {
    const q = query(
      collection(db, 'invoices'),
      where('created_by', '==', contractorId),
      ...constraints
    );
    return await getDocs(q);
  }
}

export async function dualDeleteInvoice(
  contractorId: string,
  invoiceId: string,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    await deleteDoc(doc(db, `contractors/${contractorId}/invoices/${invoiceId}`));
  }
  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    await deleteDoc(doc(db, `invoices/${invoiceId}`));
  }
}

// ============================================
// RECEIPTS COLLECTION
// ============================================

export async function dualWriteReceipt(
  contractorId: string,
  receiptId: string,
  receiptData: DocumentData,
  options: DualWriteOptions = {}
) {
  const dataWithTimestamp = {
    ...receiptData,
    updatedAt: Timestamp.now(),
  };

  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/receipts/${receiptId}`);
    await setDoc(newRef, dataWithTimestamp, { merge: true });
  }

  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `receipts/${receiptId}`);
    await setDoc(oldRef, {
      ...dataWithTimestamp,
      created_by: contractorId,
    }, { merge: true });
  }
}

export async function dualReadReceipts(
  contractorId: string,
  constraints: QueryConstraint[] = []
) {
  if (READ_MODE === 'new') {
    const q = query(
      collection(db, `contractors/${contractorId}/receipts`),
      ...constraints
    );
    return await getDocs(q);
  } else {
    const q = query(
      collection(db, 'receipts'),
      where('created_by', '==', contractorId),
      ...constraints
    );
    return await getDocs(q);
  }
}

export async function dualDeleteReceipt(
  contractorId: string,
  receiptId: string,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    await deleteDoc(doc(db, `contractors/${contractorId}/receipts/${receiptId}`));
  }
  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    await deleteDoc(doc(db, `receipts/${receiptId}`));
  }
}

// ============================================
// PURCHASE ORDERS COLLECTION
// ============================================

export async function dualWritePurchaseOrder(
  contractorId: string,
  poId: string,
  poData: DocumentData,
  options: DualWriteOptions = {}
) {
  const dataWithTimestamp = {
    ...poData,
    updatedAt: Timestamp.now(),
  };

  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/purchase_orders/${poId}`);
    await setDoc(newRef, dataWithTimestamp, { merge: true });
  }

  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `purchase_orders/${poId}`);
    await setDoc(oldRef, {
      ...dataWithTimestamp,
      created_by: contractorId,
    }, { merge: true });
  }
}

export async function dualReadPurchaseOrders(
  contractorId: string,
  constraints: QueryConstraint[] = []
) {
  if (READ_MODE === 'new') {
    const q = query(
      collection(db, `contractors/${contractorId}/purchase_orders`),
      ...constraints
    );
    return await getDocs(q);
  } else {
    const q = query(
      collection(db, 'purchase_orders'),
      where('created_by', '==', contractorId),
      ...constraints
    );
    return await getDocs(q);
  }
}

export async function dualDeletePurchaseOrder(
  contractorId: string,
  poId: string,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    await deleteDoc(doc(db, `contractors/${contractorId}/purchase_orders/${poId}`));
  }
  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    await deleteDoc(doc(db, `purchase_orders/${poId}`));
  }
}

// ============================================
// WAIVERS COLLECTION
// ============================================

export async function dualWriteWaiver(
  contractorId: string,
  waiverId: string,
  waiverData: DocumentData,
  options: DualWriteOptions = {}
) {
  const dataWithTimestamp = {
    ...waiverData,
    updatedAt: Timestamp.now(),
  };

  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/waivers/${waiverId}`);
    await setDoc(newRef, dataWithTimestamp, { merge: true });
  }

  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `waivers/${waiverId}`);
    await setDoc(oldRef, {
      ...dataWithTimestamp,
      created_by: contractorId,
    }, { merge: true });
  }
}

export async function dualReadWaivers(
  contractorId: string,
  constraints: QueryConstraint[] = []
) {
  if (READ_MODE === 'new') {
    const q = query(
      collection(db, `contractors/${contractorId}/waivers`),
      ...constraints
    );
    return await getDocs(q);
  } else {
    const q = query(
      collection(db, 'waivers'),
      where('created_by', '==', contractorId),
      ...constraints
    );
    return await getDocs(q);
  }
}

export async function dualDeleteWaiver(
  contractorId: string,
  waiverId: string,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    await deleteDoc(doc(db, `contractors/${contractorId}/waivers/${waiverId}`));
  }
  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    await deleteDoc(doc(db, `waivers/${waiverId}`));
  }
}

// ============================================
// CONTRACTS COLLECTION
// ============================================

export async function dualWriteContract(
  contractorId: string,
  contractId: string,
  contractData: DocumentData,
  options: DualWriteOptions = {}
) {
  const dataWithTimestamp = {
    ...contractData,
    updatedAt: Timestamp.now(),
  };

  if (!options.skipNewWrite) {
    const newRef = doc(db, `contractors/${contractorId}/contracts/${contractId}`);
    await setDoc(newRef, dataWithTimestamp, { merge: true });
  }

  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    const oldRef = doc(db, `contracts/${contractId}`);
    await setDoc(oldRef, {
      ...dataWithTimestamp,
      created_by: contractorId,
    }, { merge: true });
  }
}

export async function dualReadContracts(
  contractorId: string,
  constraints: QueryConstraint[] = []
) {
  if (READ_MODE === 'new') {
    const q = query(
      collection(db, `contractors/${contractorId}/contracts`),
      ...constraints
    );
    return await getDocs(q);
  } else {
    const q = query(
      collection(db, 'contracts'),
      where('created_by', '==', contractorId),
      ...constraints
    );
    return await getDocs(q);
  }
}

export async function dualDeleteContract(
  contractorId: string,
  contractId: string,
  options: DualWriteOptions = {}
) {
  if (!options.skipNewWrite) {
    await deleteDoc(doc(db, `contractors/${contractorId}/contracts/${contractId}`));
  }
  if (ENABLE_DUAL_WRITE && !options.skipOldWrite) {
    await deleteDoc(doc(db, `contracts/${contractId}`));
  }
}

/**
 * Switch read mode to new structure
 * Call this after migration is complete
 */
export function switchToNewStructure() {
  console.log('⚠️ Switching to new Firestore structure...');
  // Note: In production, this should be an environment variable or config change
  // Not a runtime function call
}

// ============================================
// SEND TO CLIENT FUNCTIONS
// ============================================

/**
 * Send an invoice to a client
 * Creates a record in client_invoices collection and sends email notification
 */
export async function sendInvoiceToClient(
  contractorId: string,
  invoiceId: string,
  clientId: string,
  invoiceData: DocumentData
) {
  const clientInvoiceRef = doc(collection(db, 'client_invoices'));

  await setDoc(clientInvoiceRef, {
    invoice_id: invoiceId,
    invoice_number: invoiceData.invoice_number,
    contractor_id: contractorId,
    client_id: clientId,
    client_name: invoiceData.client_name,
    client_email: invoiceData.client_email,
    project_title: invoiceData.title || invoiceData.project_title,
    project_description: invoiceData.description || invoiceData.project_description,
    subtotal: invoiceData.subtotal,
    tax_rate: invoiceData.tax_rate,
    tax_amount: invoiceData.tax_amount,
    total_amount: invoiceData.total_amount,
    due_date: invoiceData.due_date,
    line_items: invoiceData.line_items,
    notes: invoiceData.notes,
    pdf_url: invoiceData.pdf_url,
    status: 'sent',
    sent_at: Timestamp.now(),
    created_at: Timestamp.now(),
  });

  // Update original invoice status
  await dualWriteInvoice(contractorId, invoiceId, {
    status: 'sent',
    sent_at: Timestamp.now(),
    sent_to_client_id: clientId,
  });

  // Send email notification (don't wait, fire and forget)
  if (invoiceData.client_email) {
    sendDocumentEmailFn({
      clientEmail: invoiceData.client_email,
      clientName: invoiceData.client_name,
      documentType: 'invoice',
      documentNumber: invoiceData.invoice_number,
      documentTitle: invoiceData.title || invoiceData.project_title,
      amount: invoiceData.total_amount,
      documentId: clientInvoiceRef.id,
    }).catch(err => console.error('Failed to send invoice email:', err));
  }

  return clientInvoiceRef.id;
}

/**
 * Send a receipt to a client
 * Creates a record in client_receipts collection and sends email notification
 */
export async function sendReceiptToClient(
  contractorId: string,
  receiptId: string,
  clientId: string,
  receiptData: DocumentData
) {
  const clientReceiptRef = doc(collection(db, 'client_receipts'));

  await setDoc(clientReceiptRef, {
    receipt_id: receiptId,
    receipt_number: receiptData.receipt_number,
    contractor_id: contractorId,
    client_id: clientId,
    client_name: receiptData.client_name,
    client_email: receiptData.client_email,
    description: receiptData.description,
    payment_date: receiptData.payment_date,
    payment_method: receiptData.payment_method,
    amount_received: receiptData.amount_received,
    notes: receiptData.notes,
    pdf_url: receiptData.pdf_url,
    status: 'sent',
    sent_at: Timestamp.now(),
    created_at: Timestamp.now(),
  });

  // Update original receipt status
  await dualWriteReceipt(contractorId, receiptId, {
    status: 'sent',
    sent_at: Timestamp.now(),
    sent_to_client_id: clientId,
  });

  // Send email notification (don't wait, fire and forget)
  if (receiptData.client_email) {
    sendDocumentEmailFn({
      clientEmail: receiptData.client_email,
      clientName: receiptData.client_name,
      documentType: 'receipt',
      documentNumber: receiptData.receipt_number,
      documentTitle: receiptData.description,
      amount: receiptData.amount_received,
      documentId: clientReceiptRef.id,
    }).catch(err => console.error('Failed to send receipt email:', err));
  }

  return clientReceiptRef.id;
}

/**
 * Send a waiver to a client for signing
 * Creates a record in client_waivers collection and sends email notification
 */
export async function sendWaiverToClient(
  contractorId: string,
  waiverId: string,
  clientId: string,
  waiverData: DocumentData
) {
  const clientWaiverRef = doc(collection(db, 'client_waivers'));

  await setDoc(clientWaiverRef, {
    waiver_id: waiverId,
    waiver_number: waiverData.waiver_number,
    contractor_id: contractorId,
    client_id: clientId,
    client_name: waiverData.client_name,
    client_email: waiverData.client_email,
    title: waiverData.title,
    content: waiverData.content,
    effective_date: waiverData.effective_date,
    job_address: waiverData.job_address,
    contractor_name: waiverData.contractor_name,
    contractor_company: waiverData.contractor_company,
    pdf_url: waiverData.pdf_url,
    status: 'pending',
    sent_at: Timestamp.now(),
    created_at: Timestamp.now(),
  });

  // Update original waiver status
  await dualWriteWaiver(contractorId, waiverId, {
    status: 'sent',
    sent_at: Timestamp.now(),
    sent_to_client_id: clientId,
  });

  // Send email notification (don't wait, fire and forget)
  if (waiverData.client_email) {
    sendDocumentEmailFn({
      clientEmail: waiverData.client_email,
      clientName: waiverData.client_name,
      documentType: 'waiver',
      documentNumber: waiverData.waiver_number,
      documentTitle: waiverData.title,
      amount: null,
      documentId: clientWaiverRef.id,
    }).catch(err => console.error('Failed to send waiver email:', err));
  }

  return clientWaiverRef.id;
}

/**
 * Send a contract to a client for signing
 * Creates a record in client_contracts collection and sends email notification
 */
export async function sendContractToClient(
  contractorId: string,
  contractId: string,
  clientId: string,
  contractData: DocumentData
) {
  const clientContractRef = doc(collection(db, 'client_contracts'));

  await setDoc(clientContractRef, {
    contract_id: contractId,
    contract_number: contractData.contract_number,
    contractor_id: contractorId,
    client_id: clientId,
    client_name: contractData.client_name,
    client_email: contractData.client_email,
    client_address: contractData.client_address,
    contract_title: contractData.contract_title || contractData.title,
    contract_terms: contractData.contract_terms || contractData.content,
    start_date: contractData.start_date,
    end_date: contractData.end_date,
    total_amount: contractData.total_amount || contractData.contract_value,
    payment_terms: contractData.payment_terms,
    contractor_name: contractData.contractor_name,
    contractor_company: contractData.contractor_company,
    contractor_signature_url: contractData.contractor_signature_url,
    contractor_signed_at: contractData.contractor_signed_at,
    pdf_url: contractData.pdf_url,
    status: 'pending_client',
    sent_at: Timestamp.now(),
    created_at: Timestamp.now(),
  });

  // Update original contract status
  await dualWriteContract(contractorId, contractId, {
    status: 'sent',
    sent_at: Timestamp.now(),
    sent_to_client_id: clientId,
  });

  // Send email notification (don't wait, fire and forget)
  if (contractData.client_email) {
    sendDocumentEmailFn({
      clientEmail: contractData.client_email,
      clientName: contractData.client_name,
      documentType: 'contract',
      documentNumber: contractData.contract_number,
      documentTitle: contractData.contract_title || contractData.title,
      amount: contractData.total_amount || contractData.contract_value,
      documentId: clientContractRef.id,
    }).catch(err => console.error('Failed to send contract email:', err));
  }

  return clientContractRef.id;
}

// ============================================
// DATA ARCHIVE COLLECTIONS (for training/RAG)
// ============================================

/**
 * Document Extraction Archive
 * Stores full OCR text and extracted data from documents for training/RAG
 */
export interface DocumentExtractionArchive {
  id?: string;
  documentId: string;
  contractorId: string;
  jobId?: string;
  documentType: string;
  fileName: string;
  storageUrl: string;
  // OCR/Extraction data
  rawText: string;
  extractedFields: Record<string, any>;
  confidence: number;
  extractionMethod: 'gpt4_vision' | 'regex_fallback' | 'manual';
  // Metadata
  mimeType?: string;
  fileSize?: number;
  pageCount?: number;
  // Feedback for training
  userFeedback?: {
    isAccurate: boolean;
    corrections?: Record<string, any>;
    feedbackAt: any;
    feedbackBy: string;
  };
  createdAt: any;
  updatedAt: any;
}

/**
 * Archive document extraction data for training/RAG
 */
export async function archiveDocumentExtraction(
  extractionData: Omit<DocumentExtractionArchive, 'id' | 'createdAt' | 'updatedAt'>
) {
  const archiveRef = doc(collection(db, 'document_extractions'));
  const timestamp = Timestamp.now();

  await setDoc(archiveRef, {
    ...extractionData,
    createdAt: timestamp,
    updatedAt: timestamp,
  });

  return archiveRef.id;
}

/**
 * Add user feedback to a document extraction (for training improvement)
 */
export async function addExtractionFeedback(
  extractionId: string,
  feedback: {
    isAccurate: boolean;
    corrections?: Record<string, any>;
    feedbackBy: string;
  }
) {
  const extractionRef = doc(db, 'document_extractions', extractionId);
  await setDoc(extractionRef, {
    userFeedback: {
      ...feedback,
      feedbackAt: Timestamp.now(),
    },
    updatedAt: Timestamp.now(),
  }, { merge: true });
}

/**
 * Compliance Run Archive
 * Stores historical compliance check results for analytics and training
 */
export interface ComplianceRunArchive {
  id?: string;
  runId: string;
  contractorId: string;
  jobId: string;
  // Job context at time of check
  jobSnapshot: {
    title: string;
    location: string;
    permitType?: string;
    jobScope?: string;
    jurisdictionId?: number;
    jurisdictionCode?: string;
  };
  // Compliance results
  status: 'pass' | 'fail' | 'needs_review';
  score: number;
  summary: {
    critical: number;
    warning: number;
    info: number;
    total: number;
  };
  findings: Array<{
    ruleCode: string;
    severity: string;
    title: string;
    description: string;
    suggestedFix?: string;
    citations?: any[];
  }>;
  // Documents checked
  documentsChecked: Array<{
    documentId: string;
    documentType: string;
    fileName: string;
  }>;
  // Performance metrics
  runDurationMs: number;
  // Feedback for training
  userFeedback?: {
    wasHelpful: boolean;
    findingsAccurate: boolean;
    comments?: string;
    feedbackAt: any;
    feedbackBy: string;
  };
  createdAt: any;
}

/**
 * Archive compliance run results for analytics and training
 */
export async function archiveComplianceRun(
  runData: Omit<ComplianceRunArchive, 'id' | 'createdAt'>
) {
  const archiveRef = doc(collection(db, 'compliance_runs'));
  const timestamp = Timestamp.now();

  await setDoc(archiveRef, {
    ...runData,
    createdAt: timestamp,
  });

  return archiveRef.id;
}

/**
 * Add user feedback to a compliance run (for training improvement)
 */
export async function addComplianceRunFeedback(
  runArchiveId: string,
  feedback: {
    wasHelpful: boolean;
    findingsAccurate: boolean;
    comments?: string;
    feedbackBy: string;
  }
) {
  const runRef = doc(db, 'compliance_runs', runArchiveId);
  await setDoc(runRef, {
    userFeedback: {
      ...feedback,
      feedbackAt: Timestamp.now(),
    },
  }, { merge: true });
}

/**
 * Query compliance runs for analytics
 */
export async function getComplianceRunsForJob(jobId: string) {
  const q = query(
    collection(db, 'compliance_runs'),
    where('jobId', '==', jobId),
    orderBy('createdAt', 'desc')
  );
  return getDocs(q);
}

/**
 * Query document extractions for a contractor
 */
export async function getDocumentExtractions(
  contractorId: string,
  constraints: QueryConstraint[] = []
) {
  const q = query(
    collection(db, 'document_extractions'),
    where('contractorId', '==', contractorId),
    ...constraints
  );
  return getDocs(q);
}
